import { Dispatch, KeyboardEvent, SetStateAction, useContext, useMemo, useState } from 'react';

import { useApolloClient } from '@apollo/client/react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { Formik } from 'formik';
import { BsLink45Deg } from 'react-icons/bs';
import { FiSend } from 'react-icons/fi';
import { MdAttachFile } from 'react-icons/md';
import { TbCarCrash } from 'react-icons/tb';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { Customer } from '../../../gql/customerGql';
import { Deal, DealStateEnum } from '../../../gql/dealGql';
import {
  DealContact,
  DealMediaTypeEnum,
  GetUploadUrlDocument,
  Maybe,
  OriginalLessee,
  useCreateEmbeddedInsuranceLeadAndGetLinkMutation,
  useFileSendMutation,
  useMediaInsertMutation,
  useMessageSendMutation,
  useSuggestedMessageLazyQuery,
} from '../../../gql/generated/graphql';
import { MediaListObj } from '../../../gql/messageGql';

import Tooltip from '../../shared/Tooltip';
import { ChatMessage } from '../TextModal';

import FileSelect from './FileSelect';
import SpudacusSuggestButton from './SpudacusSuggestButton';
import TemplateSelect from './TemplateSelect';

import { marketUrl } from '../../../config';
import { LDFlags } from '../../../constants/experiments';
import { PermissionEnum } from '../../../constants/permissions';
import useFlag from '../../../hooks/useFlag';
import { useUser } from '../../../hooks/useUser';
import { AbilityContext, NotesContext } from '../../../libs/contextLib';
import { formatPhoneWithParentheses } from '../../../libs/utils';
import { DealMediaEdit, GetUploadUrlResult } from '../../../types/media';
import { ACCEPT_MIME_STRING, filterValidFiles } from '../../../utils/fileValidation';
import { getFileNameAndExtension, normalizeFileName } from '../../../utils/files';
import { transformMetadata } from '../../../utils/media';
import { isAdvisor, isFinancialSpecialist, isLeadAdvisor } from '../../../utils/permissions';
import CopyButtonV2 from '../../CopyButton/CopyButtonV2';

const embeddedInsuranceValidationSchema = yup.object({
  customer: yup.object({
    first_name: yup.string().nullable().required('First Name is missing'),
    last_name: yup.string().nullable().required('Last Name is missing'),
    dob: yup.string().nullable().required('Date of Birth is missing'),
    phone_number: yup.string().nullable().required('Phone Number is missing'),
    email: yup.string().nullable().required('Email is missing'),

    address: yup.object({
      address_line: yup.string().nullable().required('Address is missing'),
      address_line_2: yup.string().nullable(),
      city: yup.string().nullable().required('City is missing'),
      state: yup.string().nullable().required('State is missing'),
      zip: yup.string().nullable().required('ZIP Code is missing'),
    }),
  }),
  car: yup.object({
    make: yup.string().nullable().required('Make is missing'),
    model: yup.string().nullable().required('Model is missing'),
    year: yup.string().nullable().required('Year is missing'),
  }),
});

const validateEmbeddedInsurance = (deal: Deal | null): string[] => {
  try {
    embeddedInsuranceValidationSchema.validateSync(deal, { abortEarly: false });
    return [];
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      return error.errors;
    }
    return ['Validation error occurred'];
  }
};

type MessageInputProps = {
  deal: Deal | null;
  phone: string;
  recipient: Maybe<Customer | DealContact | OriginalLessee>;
  messageList: ChatMessage[];
  setMessageList: Dispatch<SetStateAction<ChatMessage[]>>;
};

const MAX_LENGTH = 1400;
const MAX_FILE_SIZE_IN_BYTES = 4194304; // 4MB

/**
 * Validates and filters files by extension, MIME type, and size.
 * Size validation is done manually here because MessageInput uses a native
 * <input type="file"> instead of the Dropzone component, which handles
 * size rejection automatically via its `maxSize` prop.
 */
const filterAndValidateFiles = (fileArr: File[]): File[] =>
  filterValidFiles(fileArr).filter((f) => {
    if (f.size > MAX_FILE_SIZE_IN_BYTES) {
      toast.error(`${f.name} can't be sent because it is bigger than 4MB.`);
      return false;
    }
    return true;
  });

const MessageInput = ({
  deal,
  phone,
  recipient,
  messageList,
  setMessageList,
}: MessageInputProps) => {
  const client = useApolloClient();
  const user = useUser();
  const abilities = useContext(AbilityContext);
  const isAdmin = abilities.has(PermissionEnum.SuperUser);

  const { notesRefetch } = useContext(NotesContext);

  const [sendFile] = useFileSendMutation();
  const [mediaInsert] = useMediaInsertMutation();
  const [sendMessage] = useMessageSendMutation();
  const [getEmbeddedInsuranceLink] = useCreateEmbeddedInsuranceLeadAndGetLinkMutation();
  const [getSuggestedMessage, { loading: suggestedMessageLoading }] =
    useSuggestedMessageLazyQuery();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<DealMediaEdit[]>([]);
  const [shouldWriteInput, setShouldWriteInput] = useState(true);

  const suggestedMessageFlag = useFlag(LDFlags.SUGGESTED_MESSAGE_BUTTON);
  const embeddedInsuranceEnabled = useFlag(LDFlags.EMBEDDED_INSURANCE);

  const onClear = (media: DealMediaEdit, notSent?: boolean) => {
    if (notSent) {
      setFiles(files.filter((f) => f.fileName !== media.fileName));
    } else {
      setFiles([]);
    }
  };

  const onFilesSelected = async (fileList: FileList) => {
    const fileArr: File[] = Array.from(fileList);
    const validatedFiles = filterAndValidateFiles(fileArr);
    const selectedMedia: DealMediaEdit[] = await Promise.all(
      validatedFiles.map(async (file) => {
        const normalizedFileName = normalizeFileName(file.name);
        const { fileName, extension } = getFileNameAndExtension(normalizedFileName);

        return {
          selectedFile: file,
          sourceFileName: normalizedFileName,
          fileName,
          extension,
          type: DealMediaTypeEnum.Other,
        };
      }),
    );
    setFiles(selectedMedia);
  };

  const addMessageOnSend = (
    body?: Maybe<string>,
    media?: Maybe<string>,
    fileType?: Maybe<string>,
  ) => {
    return {
      date: new Date(),
      author: user.name,
      fromCustomer: false,
      data: {
        text: body ?? '',
        dateCreated: new Date()
          .toLocaleString('en-US', {
            day: 'numeric',
            month: 'short',
            weekday: 'short',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
          })
          .replace(/,/g, (a, b) => {
            return b > 5 ? ' at' : ',';
          }),
        mediaListObj: media
          ? {
              data: [
                {
                  url: media ?? '',
                  contentType: fileType === '.pdf' ? 'application/pdf' : 'image/jpeg',
                },
              ],
            }
          : null,
      },
    } as ChatMessage;
  };

  const onMessageSend = async (message: ChatMessage) => {
    setLoading(true);
    if (files && files.length) {
      await Promise.all(
        files.map(async (file) => {
          if (!deal?.id) {
            return;
          }

          const { data } = await client.query<GetUploadUrlResult>({
            query: GetUploadUrlDocument,
            variables: {
              dealId: deal.id,
              fileName: file.sourceFileName,
            },
          });

          const { url, key } = data.getUploadUrl;
          await fetch(url, {
            method: 'PUT',
            body: file.selectedFile,
          });

          await mediaInsert({
            variables: {
              key,
              dealId: deal.id,
              type: file.type,
              metadata: transformMetadata(file.metadata),
            },
          }).then((res) => {
            return sendFile({
              variables: {
                file: key,
                dealPhoneNumber: phone,
                id: deal.id ?? -1,
              },
            })
              .then(() => {
                const newMessage = addMessageOnSend(
                  undefined,
                  res.data?.mediaInsert?.signed_url,
                  res.data?.mediaInsert?.key?.slice(-4),
                );
                messageList.push(newMessage);
                toast.success(`Successfully sent: ${file.sourceFileName}`);
                onClear(file, false);
                notesRefetch();
              })
              .catch((e) => toast.error(`Couldn't send ${file.sourceFileName}. ${e.message}`));
          });
        }),
      );
    }
    if (message.data.text) {
      sendMessage({
        variables: {
          message: message.data.text,
          dealPhoneNumber: phone,
          id: deal?.id ?? -1,
        },
      })
        .then((result) => {
          const newText = addMessageOnSend(result.data?.messageSend?.body || message.data.text);
          setMessageList([...messageList, newText]);
          toast.success('Message Sent');
          notesRefetch();
        })
        .catch((e) => {
          toast.error(e.message);
        });
    }
    setMessageList([...messageList]);
    setLoading(false);
  };

  const callOnMessageSend = (values: { text_message: string }) => {
    onMessageSend({
      date: new Date(),
      fromCustomer: false,
      author: 'me',
      data: {
        text: values.text_message,
        mediaListObj: files.length
          ? ({
              data: [
                {
                  url: files[0].signed_url,
                  contentType: files[0].fileName,
                },
              ],
            } as MediaListObj)
          : null,
      },
    });
  };

  const getSpudacusSuggestedMessage = async (
    draftMessage: string,
    setFieldValue: (field: string, value: string) => void,
  ) => {
    if (!deal?.id || !recipient?.id) {
      return;
    }

    const { data } = await getSuggestedMessage({
      variables: {
        customerId: recipient.id,
        dealId: deal.id,
        phoneNumber: phone,
        draftMessage,
      },
      fetchPolicy: 'network-only',
    });

    if (data?.suggestedMessage) {
      setFieldValue('text_message', data?.suggestedMessage);
    }
  };

  const canSeeShareLink =
    isAdmin || isAdvisor(abilities) || isLeadAdvisor(abilities) || isFinancialSpecialist(abilities);

  const shareLink = (() => {
    const userId = user.id.split('|')[1];

    let link: string = marketUrl;
    if (isAdmin || isAdvisor(abilities)) {
      link += `?advisor_id=${userId}`;
    } else if (isFinancialSpecialist(abilities)) {
      link += `?financial_specialist_id=${userId}`;
    }

    return link;
  })();

  const getEmbeddedInsuranceLinkApiCall = async () => {
    if (!deal?.id) {
      return '';
    }

    const response = await getEmbeddedInsuranceLink({
      variables: { dealId: deal.id },
    });
    return response.data?.createEmbeddedInsuranceLeadAndGetLink?.url ?? '';
  };

  const isBuyer = recipient?.id === deal?.customer?.id;

  const canGenerateEmbeddedInsuranceLink =
    embeddedInsuranceEnabled && isBuyer && deal?.state !== DealStateEnum.Estimate;

  const embeddedInsuranceValidationErrors = useMemo(
    () => (canGenerateEmbeddedInsuranceLink ? validateEmbeddedInsurance(deal) : []),
    [canGenerateEmbeddedInsuranceLink, deal],
  );

  return (
    <Formik
      initialValues={{ text_message: '' }}
      onSubmit={() => undefined}
      key={recipient?.id} // This replaces `enableReinitialize` to clear the input field when the recipient changes
    >
      {({ setFieldValue, values, handleChange }) => (
        <Box
          bgColor="cadetBlue"
          w="100%"
          h="35%"
          p={2}
          borderRadius="0px 0px 15px"
          margin={0}
          borderColor="gray.200"
        >
          <Textarea
            resize="none"
            h="57% !important"
            size="lg"
            value={values.text_message}
            border={0}
            borderRadius="10px"
            bgColor="white"
            placeholder="Write a message..."
            name="text_message"
            fontSize="16px"
            maxLength={MAX_LENGTH}
            onChange={shouldWriteInput ? handleChange : undefined}
            onWheel={(e) => {
              // Stops the wheel event from bubbling up, avoiding scroll conflicts with parent elements.
              e.stopPropagation();
            }}
            onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.shiftKey || e.key !== 'Enter') {
                setShouldWriteInput(true);
              } else {
                setShouldWriteInput(false);
                callOnMessageSend(values);
                setFieldValue('text_message', '');
              }
            }}
          />
          <Flex justifyContent="space-between">
            <Text fontSize="sm" mt={1}>
              {`${values.text_message.length || 0}/${MAX_LENGTH} characters`}
            </Text>
            <Text fontSize="sm" mt={1}>
              Text Number: {formatPhoneWithParentheses(deal?.assigned_sms_phone_number) || '-'}
            </Text>
          </Flex>
          <HStack justifyContent="space-between">
            <HStack h="60px" overflowY="visible">
              <Input
                type="file"
                display="none"
                id="select-files"
                multiple
                accept={ACCEPT_MIME_STRING}
                onChange={(e) => {
                  if (e.target.files) {
                    onFilesSelected(e.target.files);
                  }
                }}
              />
              <TemplateSelect deal={deal} />
              <IconButton
                aria-label="Add File"
                variant="secondary"
                size="sm"
                onClick={() => document.getElementById('select-files')?.click()}
                icon={<Icon as={MdAttachFile} boxSize="20px" />}
              />
              {canSeeShareLink && (
                <CopyButtonV2
                  size="sm"
                  variant="secondary"
                  value={shareLink}
                  icon={<BsLink45Deg size="20px" />}
                />
              )}
              {canGenerateEmbeddedInsuranceLink && (
                <Tooltip errors={embeddedInsuranceValidationErrors}>
                  <CopyButtonV2
                    getValueAsync={getEmbeddedInsuranceLinkApiCall}
                    size="sm"
                    variant="secondary"
                    icon={<TbCarCrash size="20px" />}
                    disabled={embeddedInsuranceValidationErrors.length > 0}
                  />
                </Tooltip>
              )}
              {suggestedMessageFlag && (
                <SpudacusSuggestButton
                  isComposingMessage={values.text_message.length > 0}
                  onClick={() => getSpudacusSuggestedMessage(values.text_message, setFieldValue)}
                  isLoading={suggestedMessageLoading}
                />
              )}
              {files.map((media, index) => {
                if (index < 3) {
                  return <FileSelect media={media} onClear={() => onClear(media, true)} />;
                }
                if (index === 3) {
                  return (
                    <Box
                      position="relative"
                      bgColor="blue.300"
                      border="1px solid"
                      borderColor="blue.400"
                      borderRadius="5px"
                      w="40px"
                      h="40px"
                      fontSize="16px"
                      display="flex"
                      justifyContent="center"
                    >
                      <Box alignSelf="center">+{files.length - 3}</Box>
                    </Box>
                  );
                }
                return null;
              })}
            </HStack>
            <Button
              size="md"
              isDisabled={!values.text_message && files.length < 1}
              leftIcon={<Icon as={FiSend} boxSize="20px" />}
              onClick={() => {
                callOnMessageSend(values);
                setFieldValue('text_message', '');
              }}
            >
              {!loading ? 'SEND' : <Spinner />}
            </Button>
          </HStack>
        </Box>
      )}
    </Formik>
  );
};

export default MessageInput;
